import type { Row } from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";

import Button from "veneer/Button";
import { JollyRancherChip } from "veneer/Chip";
import DataTable, { createColumnHelper, createFilter } from "veneer/DataTable";

import { getRoleProp } from "cortex/ui/src/constant/userList";
import { useAppSelector, useFeatureFlag } from "cortex/ui/src/hooks";
import { Network } from "cortex/ui/src/network/model";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import {
  isAdmin,
  isFullAdmin,
  usersSelector,
  usersStoresLoadedSelector,
} from "cortex/ui/src/redux-store/users/selectors";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import {
  Filter,
  FormAction,
  ROLE,
  User,
  UserModalType,
} from "cortex/ui/src/user/model";
import { Container } from "cortex/ui/src/view/layout/Container";

import UserFormDrawer from "./UserFormDrawer";
import UserListFilters from "./UserListFilters";

const getAllUsersAsArray = makeGetAllAsArray<User>();
const getAllNetworksAsArray = makeGetAllAsArray<Network>();
const basePath = "/users";
const filter = createFilter<User>("email", "name");
const column = createColumnHelper<User>();

function userRoleSort(rowA: Row<User>, rowB: Row<User>): number {
  const roleA = getRoleProp(rowA.original).label.toLowerCase();
  const roleB = getRoleProp(rowB.original).label.toLowerCase();

  if (roleA < roleB) return -1;
  if (roleA > roleB) return 1;
  return 0;
}

export default function UserList() {
  const { action, id } = useParams<{ action?: FormAction; id?: string }>();
  const history = useHistory();
  const usersById = useAppSelector(usersSelector);
  const users: ReadonlyArray<User> = getAllUsersAsArray({ byId: usersById });
  const user = useAppSelector(getAuthUser);
  const userIsAdmin = isFullAdmin(user);
  const networksById = useAppSelector(networksSelector);
  const networks: ReadonlyArray<Network> = useMemo(
    () => getAllNetworksAsArray({ byId: networksById }),
    [networksById],
  );
  const networkOptions = useMemo(() => {
    return networks
      .map((network) => ({
        label: network.name,
        value: String(network.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [networks]);
  const ssoEnabled = useFeatureFlag("cortex-internal-sso");
  const userStoresLoaded = useAppSelector(usersStoresLoadedSelector);
  const networkStoresLoaded = useAppSelector(networksStoresLoadedSelector);
  const storesLoaded = userStoresLoaded && networkStoresLoaded;
  const [userModelType, setUserModalType] = useState<UserModalType>(
    UserModalType.NONE,
  );
  const [filters, setFilters] = useState<Filter>(() => ({
    roles: [],
  }));
  const openUserForm = useMemo(
    () => userModelType !== UserModalType.NONE,
    [userModelType],
  );

  const filteredRows = useMemo(() => {
    if (!filters.roles || filters.roles.length === 0) {
      return users;
    }

    const hasDisabledFilter = filters.roles.includes("D");
    const activeRoleFilters = filters.roles.filter(
      (r): r is Exclude<User["role"], typeof ROLE.DISABLED> =>
        r !== ROLE.DISABLED,
    );

    return users.filter((user) => {
      const matchesDisabled = hasDisabledFilter && user.disabled === true;

      const matchesRole =
        activeRoleFilters.length > 0 &&
        activeRoleFilters.includes(
          user.role as Exclude<User["role"], typeof ROLE.DISABLED>,
        );

      return matchesDisabled || matchesRole;
    });
  }, [filters, users]);

  useEffect(() => {
    if (!userIsAdmin) {
      history.replace("/404");
      return;
    } else if (action == FormAction.CREATE) {
      setUserModalType(UserModalType.CREATE);
      return;
    } else if (action == FormAction.EDIT && id && usersById[id]) {
      setUserModalType(UserModalType.EDIT);
      return;
    }
    setUserModalType(UserModalType.NONE);
  }, [action, id, history, userIsAdmin, usersById]);

  const handleCloseModal = () => {
    history.push(basePath);
  };

  const columns = useMemo(
    () => [
      column.accessor("name", {
        header: "Name",
        cell: (prop) => {
          const value = prop.getValue();
          if (!userIsAdmin) {
            return value;
          }
          return (
            <Link to={`${basePath}/${prop.row.original.id}/edit`}>{value}</Link>
          );
        },
        meta: {
          primary: true,
        },
        sortingFn: "alphanumeric",
      }),
      column.accessor("email", {
        header: "Email",
        cell: (prop) => prop.getValue(),
        sortingFn: "alphanumeric",
      }),
      column.accessor("role", {
        header: "Access Level",
        cell: (prop) => {
          const record = prop.row.original;
          const { label, labelColor } = getRoleProp(record);
          return <JollyRancherChip color={labelColor} label={label} />;
        },
        sortingFn: userRoleSort,
      }),
      column.accessor("networks", {
        header: "Networks",
        cell: (prop) => {
          const record = prop.row.original;
          if (isAdmin(record)) {
            return "All networks";
          }

          const userNetworks = (prop.getValue() ?? [])
            .map((networkId) => networksById[networkId])
            .filter((n) => n)
            .map((n) => {
              const { id, name } = n;

              return (
                <li key={id}>
                  <Link to={`/networks/${id}`}>{name}</Link>
                </li>
              );
            });

          return <ul className="list-unstyled">{userNetworks}</ul>;
        },
        enableSorting: false,
      }),
    ],
    [networks, userIsAdmin],
  );

  const escapeCSV = (value: string | number) => {
    const str = String(value).replace(/"/g, '""'); // escape quotes
    return `"${str}"`;
  };

  const handleExport = () => {
    if (!filteredRows.length) {
      downloadCSV("No data", "user-list.csv");
      return;
    }

    const headers = ["Name", "Email", "Access Level", "Networks"];

    const userData = filteredRows.map((user) => {
      let networksStr = "";

      if (Array.isArray(user.networks)) {
        const userNetworks = user.networks.map(
          (networkId) => networksById[networkId]?.name || "",
        );
        networksStr = userNetworks.join(", ");
      }

      return [
        user.name || "",
        user.email || "",
        getRoleProp(user).label,
        isAdmin(user) ? "All Networks" : networksStr,
      ];
    });

    const data = [headers, ...userData];

    const csv = data.map((row) => row.map(escapeCSV).join(",")).join("\r\n");

    downloadCSV(csv, "user-list.csv");
  };

  const filterOptions = useMemo(
    () => <UserListFilters onChange={setFilters} value={filters} />,
    [filters],
  );

  const addButton = (
    <Link to="/users/create">
      <Button dataTestId="add-user-button">
        {ssoEnabled ? "Add User" : "Invite User"}
      </Button>
    </Link>
  );

  return (
    <Container title="Users">
      {storesLoaded ? (
        <DataTable
          columns={columns}
          exportButtonLabel="Export Users"
          filter={filter}
          initialPageSize={10}
          onExport={handleExport}
          primaryActions={addButton}
          rows={filteredRows}
          viewOptions={filterOptions}
        />
      ) : (
        <LoadingIndicator />
      )}
      {openUserForm && (
        <UserFormDrawer
          mode={userModelType === UserModalType.CREATE ? "invite" : "edit"}
          networkOptions={networkOptions}
          onCancel={handleCloseModal}
          open={openUserForm}
          ssoEnabled={ssoEnabled}
          user={
            userModelType === UserModalType.EDIT && id
              ? usersById[id]
              : undefined
          }
        />
      )}
    </Container>
  );
}

const downloadCSV = (data: string, filename: string) => {
  const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
